/**
 * Lazy Component Loader
 *
 * Dynamically imports components by name without needing a registry.
 * Uses dynamic imports to enable code splitting.
 */

import type { ComponentType } from "react";
import type { KioskComponentProps } from "../types";

/**
 * Lazy load a component by name
 *
 * Assumes components follow the naming convention:
 * - File: ../components/ComponentName.tsx
 * - Export: default export or named export matching the component name
 */
export async function loadComponent(
  componentName: string,
): Promise<ComponentType<KioskComponentProps> | undefined> {
  try {
    // Dynamic import from components directory
    const module = await import(`../components/${componentName}.tsx`);

    // Try default export first, then named export
    return module.default || module[componentName];
  } catch (error) {
    console.error(`Failed to load component "${componentName}":`, error);
    return undefined;
  }
}

/**
 * Preload multiple components at once
 */
export async function preloadComponents(
  componentNames: string[],
): Promise<Map<string, ComponentType<KioskComponentProps>>> {
  const loaded = new Map<string, ComponentType<KioskComponentProps>>();

  await Promise.all(
    componentNames.map(async (name) => {
      const component = await loadComponent(name);
      if (component) {
        loaded.set(name, component);
      }
    }),
  );

  return loaded;
}
